import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  HomeIcon,
  CubeIcon,
  VariableIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";

/**
 * Shared mobile bottom navigation for a service report and its sub-pages
 * (details, item consumption, variables, checklist, ...).
 *
 * Page-based entries (details/items/variables/checklist) always navigate to
 * the relevant route so the menu works on every report page. Dialog-based
 * entries (report, guide, time registration) call the provided handler when
 * available (on the main report view) and otherwise fall back to navigating
 * back to the report details page where those actions live.
 */
export function ReportMobileNav({
  report,
  reportVariablesHref,
  showGuide = false,
  onOpenReport,
  onOpenGuide,
  onOpenTimeRegistration,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!report?.id || report?.deleted) {
      document.documentElement.style.setProperty("--mobile-nav-height", "0px");
      return undefined;
    }

    const mq = window.matchMedia("(max-width: 1023px)");
    const syncNavHeight = () => {
      document.documentElement.style.setProperty(
        "--mobile-nav-height",
        mq.matches ? "3.75rem" : "0px"
      );
    };
    syncNavHeight();
    mq.addEventListener("change", syncNavHeight);
    return () => {
      mq.removeEventListener("change", syncNavHeight);
      document.documentElement.style.setProperty("--mobile-nav-height", "0px");
    };
  }, [report?.id, report?.deleted]);

  if (!report?.id || report?.deleted) return null;

  const reportId = report.id;
  const basePath = `/dashboard/service/reports/${reportId}`;
  const pathname = location?.pathname || "";

  const isActive = (suffix) =>
    suffix ? pathname.startsWith(`${basePath}/${suffix}`) : pathname === basePath;

  const goReportHome = () => navigate(basePath);

  const itemClass = (active) =>
    `flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-0.5 py-1.5 transition-colors active:bg-gray-100 ${
      active ? "bg-teal-50 text-teal-600" : "text-gray-600 hover:bg-gray-50"
    }`;

  const labelClass =
    "max-w-full text-center text-[10px] font-medium leading-tight";

  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-gray-200 bg-white/95 shadow-[0_-4px_14px_rgba(0,0,0,0.06)] backdrop-blur-sm supports-[backdrop-filter]:bg-white/90"
      style={{
        paddingBottom: "max(0.35rem, env(safe-area-inset-bottom, 0px))",
      }}
      aria-label={t("report.view.mobileNav.ariaLabel")}
    >
      <div className="flex items-stretch justify-around gap-0 px-0.5 pt-1">
        <button
          type="button"
          onClick={goReportHome}
          className={itemClass(isActive(""))}
        >
          <HomeIcon className="h-5 w-5 shrink-0" aria-hidden />
          <span className={labelClass}>
            {t("report.view.mobileNav.details")}
          </span>
        </button>

        {report?.report_type?.items && (
          <button
            type="button"
            onClick={() => navigate(`${basePath}/item-consumption`)}
            className={itemClass(isActive("item-consumption"))}
          >
            <CubeIcon className="h-5 w-5 shrink-0" aria-hidden />
            <span className={labelClass}>
              {t("report.view.mobileNav.items")}
            </span>
          </button>
        )}

        {report?.report_type?.variables && (
          <button
            type="button"
            onClick={() =>
              navigate(reportVariablesHref || `${basePath}/variables`)
            }
            className={itemClass(isActive("variables"))}
          >
            <VariableIcon className="h-5 w-5 shrink-0" aria-hidden />
            <span className={labelClass}>
              {t("report.view.mobileNav.variables")}
            </span>
          </button>
        )}

        {report?.report_type?.checklist && (
          <button
            type="button"
            onClick={() => navigate(`${basePath}/checklist`)}
            className={itemClass(isActive("checklist"))}
          >
            <ClipboardDocumentIcon className="h-5 w-5 shrink-0" aria-hidden />
            <span className={labelClass}>
              {t("report.view.mobileNav.checklist")}
            </span>
          </button>
        )}

        {showGuide && (
          <button
            type="button"
            onClick={onOpenGuide || goReportHome}
            className={itemClass(false)}
          >
            <SparklesIcon className="h-5 w-5 shrink-0" aria-hidden />
            <span className={labelClass}>
              {t("report.view.mobileNav.guide")}
            </span>
          </button>
        )}

        <button
          type="button"
          onClick={onOpenReport || goReportHome}
          className={itemClass(false)}
        >
          <ClipboardDocumentCheckIcon className="h-5 w-5 shrink-0" aria-hidden />
          <span className={labelClass}>
            {t("report.view.mobileNav.report")}
          </span>
        </button>

        <button
          type="button"
          onClick={onOpenTimeRegistration || goReportHome}
          className={itemClass(false)}
        >
          <ClockIcon className="h-5 w-5 shrink-0" aria-hidden />
          <span className={labelClass}>
            {t("report.view.mobileNav.timeRegistration")}
          </span>
        </button>
      </div>
    </nav>
  );
}

export default ReportMobileNav;
